/**
 * useScraperHealthCheck Hook
 *
 * Manages automatic health checks for scrapers
 * Checks health once per session when component mounts
 */

import { useState, useEffect, useRef } from 'react';
import { checkAllScrapersHealth } from '../utils/scraperHealthCheck';

// Session-level flag to track if health check has run
const healthCheckSessionCache = {
  hasRun: false,
  timestamp: null,
};

export function useScraperHealthCheck(scrapers, options = {}) {
  const {
    autoCheck = true,
    onComplete = null,
  } = options;

  const [isChecking, setIsChecking] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    // Only run automatic check if:
    // 1. autoCheck is enabled
    // 2. Haven't checked in this session yet
    // 3. Haven't checked in this component mount yet
    // 4. Scrapers are loaded
    if (
      autoCheck &&
      !healthCheckSessionCache.hasRun &&
      !hasCheckedRef.current &&
      scrapers &&
      scrapers.length > 0
    ) {
      hasCheckedRef.current = true;
      performHealthCheck();
    }
  }, [scrapers, autoCheck]);

  const performHealthCheck = async () => {
    try {
      setIsChecking(true);

      const results = await checkAllScrapersHealth(scrapers);

      // Mark that health check has run in this session
      healthCheckSessionCache.hasRun = true;
      healthCheckSessionCache.timestamp = Date.now();

      if (onComplete) {
        onComplete(results);
      }

      return results;
    } catch (error) {
      console.error('Auto health check failed:', error);
      return null;
    } finally {
      setIsChecking(false);
    }
  };

  // Manual trigger for health check
  const checkHealth = async () => {
    hasCheckedRef.current = true;
    return await performHealthCheck();
  };

  // Reset session cache (useful for testing or forcing refresh)
  const resetCache = () => {
    healthCheckSessionCache.hasRun = false;
    healthCheckSessionCache.timestamp = null;
    hasCheckedRef.current = false;
  };

  return {
    isChecking,
    checkHealth,
    resetCache,
    lastCheck: healthCheckSessionCache.timestamp,
  };
}

export default useScraperHealthCheck;
